import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { useUiDateLocale } from "../../lib/ui-date-locale";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type DatePickerProps = {
  id?: string;
  value?: Date | null;
  onChange?: (date?: Date) => void;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  "aria-describedby"?: React.AriaAttributes["aria-describedby"];
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-label"?: React.AriaAttributes["aria-label"];
  calendarDisabled?: React.ComponentProps<typeof Calendar>["disabled"];
  calendarCaptionLayout?: React.ComponentProps<typeof Calendar>["captionLayout"];
  calendarStartMonth?: Date;
  calendarEndMonth?: Date;
};

export function DatePicker({
  id,
  value,
  onChange,
  onBlur,
  placeholder = "Pick a date",
  className,
  disabled,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
  calendarDisabled,
  calendarCaptionLayout = "label",
  calendarStartMonth,
  calendarEndMonth,
}: DatePickerProps) {
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(value ?? undefined);
  const [open, setOpen] = React.useState(false);
  const dateLocale = useUiDateLocale();

  const selectedDate = value !== undefined ? value ?? undefined : internalDate;

  React.useEffect(() => {
    const incomingTime = value?.getTime();
    const internalTime = internalDate?.getTime();

    if (value !== undefined && incomingTime !== internalTime) {
      setInternalDate(value ?? undefined);
    }
  }, [internalDate, value]);

  const handleSelect = (date?: Date) => {
    if (value === undefined) {
      setInternalDate(date);
    }
    onChange?.(date);
    setOpen(false);
  };

  const triggerLabel = selectedDate ? format(selectedDate, "PPP", { locale: dateLocale }) : <span>{placeholder}</span>;
  const triggerClassName = cn(
    "data-[empty=true]:text-muted-foreground w-[280px] max-w-full justify-start text-left font-normal",
    className,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          data-empty={!selectedDate}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          onBlur={onBlur}
          className={triggerClassName}
        >
          <CalendarIcon />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={selectedDate}
          defaultMonth={selectedDate}
          onSelect={handleSelect}
          disabled={calendarDisabled}
          captionLayout={calendarCaptionLayout}
          startMonth={calendarStartMonth}
          endMonth={calendarEndMonth}
        />
      </PopoverContent>
    </Popover>
  );
}
